import { computed, Ref } from 'vue';
import { storeToRefs } from 'pinia';
import { useSearchStore } from '@stores/useSearchStore';
import type ISelectItem from '@app/interfaces/dtos/ISelectItem';
import { SelectListItemStateType } from '@app/enums/SelectListItemStateType';
import type { FilterMapper } from '@app/routing/mappers/FilterMapper';
import { FilterType } from '@studyportals/omnisearch-interfaces';
import { PortalType } from '@studyportals/domain-client';
import type IExtendedSearchQueryFilter from '@app/interfaces/dtos/IExtendedSearchQueryFilter';
import { IFacetKeyValuePair } from '@domain/interfaces/dtos/IFacetKeyValuePair';
import { getPortalType } from "@utils/get-portal-type.ts";

export const useCheckboxFilter = (mapper: FilterMapper, fType: FilterType): { items: Ref<ISelectItem[]>; handleSelectionChange: (item: ISelectItem, mState: string) => Promise<void>; } => {
	const searchStore = useSearchStore();
	const { filtersSelection, pageData, portalType } = storeToRefs(searchStore);
	const currentPortalType = getPortalType();

	const facetCounts = computed(() => {
		const filterFacets = pageData.value?.filtersFacets;
		const counts: IFacetKeyValuePair | undefined = filterFacets ? filterFacets[fType] : undefined;

		return counts;
	});

	const items = computed<ISelectItem[]>(() => {
		const filters = filtersSelection.value;
		const selectedValues = filters.find((filter) => filter.type === fType)?.values ?? [];
		const selectedIds = new Set(selectedValues.map((value) => value.id));

		return mapper.getAllExtendedFilterValues(portalType.value as PortalType)
			.filter((value) => !mapper.isImplicitForPortalType(String(value.id), currentPortalType))
			.map((value) => ({
				value: value.id,
				text: value.name || value.shortName || String(value.id),
				count: facetCounts.value?.[value.id] ?? 0,
				state: selectedIds.has(value.id)
					? SelectListItemStateType.SELECTED
					: SelectListItemStateType.ENABLED
			}));
	});

	const updateFilters = async (valueId: string, shouldSelect: boolean): Promise<void> => {
		const mappedValue = mapper.getExtendedFilterValueById(valueId);
		if (!mappedValue) return;

		const filters = filtersSelection.value;
		const currentSelection = filters.find((filter) => filter.type === fType);
		const isAlreadySelected = currentSelection?.values.some((value) => value.id === valueId) ?? false;

		if (shouldSelect && isAlreadySelected) return;
		if (!shouldSelect && !isAlreadySelected) return;

		let nextFilters: IExtendedSearchQueryFilter[] = [...filters];

		if (shouldSelect) {
			if (currentSelection) {
				const updatedSelection: IExtendedSearchQueryFilter = {
					...currentSelection,
					values: [...currentSelection.values, mappedValue]
				};

				nextFilters = nextFilters.map((filter) => filter.type === fType ? updatedSelection : filter);
			} else {
				nextFilters = [...nextFilters, { type: fType, values: [mappedValue] }];
			}
		} else if (currentSelection) {
			const remainingValues = currentSelection.values.filter((value) => value.id !== valueId);

			if (remainingValues.length > 0) {
				const updatedSelection: IExtendedSearchQueryFilter = { ...currentSelection, values: remainingValues };
				nextFilters = nextFilters.map((filter) => filter.type === fType ? updatedSelection : filter);
			} else {
				nextFilters = nextFilters.filter((filter) => filter.type !== fType);
			}
		}

		searchStore.filtersSelection = nextFilters;
		await searchStore.setRouteAndLoadData();
	};

	const handleSelectionChange = async (item: ISelectItem, mState: string): Promise<void> => {
		await updateFilters(String(item.value), mState === SelectListItemStateType.SELECTED as string);
	};

	return {
		items,
		handleSelectionChange
	};
}
